import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Headphones, Music, Check } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

/**
 * High-Intent Conversion Modal
 * 
 * Triggers: scroll 60% OR 20 seconds on page
 * Shows: once per session
 * Purpose: Force clear decision between reviewer/artist paths
 */
const IntentModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already shown this session
    const hasSeenModal = sessionStorage.getItem('mcm_intent_modal_shown');
    if (hasSeenModal) {
      return;
    }

    let timeoutId;
    let scrollTriggered = false;

    // Trigger after 20 seconds
    timeoutId = setTimeout(() => {
      if (!hasTriggered && !scrollTriggered) {
        triggerModal();
      }
    }, 20000);

    // Trigger on 60% scroll
    const handleScroll = () => {
      if (scrollTriggered || hasTriggered) return;
      
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (window.scrollY / scrollHeight) * 100;
      
      if (scrollPercent >= 60) {
        scrollTriggered = true;
        clearTimeout(timeoutId);
        triggerModal();
      }
    };

    const triggerModal = () => {
      if (hasTriggered) return;
      setHasTriggered(true);
      setIsVisible(true);
      sessionStorage.setItem('mcm_intent_modal_shown', 'true');
      // GA4: Track modal view
      trackEvent('modal_view');
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasTriggered]);

  const handleClose = () => {
    setIsVisible(false);
    // GA4: Track modal dismiss
    trackEvent('modal_dismiss');
  };

  const handleReviewerClick = () => {
    setIsVisible(false);
    // GA4: Track modal CTA click + store intent
    trackEvent('modal_cta_click', { intent: 'reviewer' });
    sessionStorage.setItem('mcm_user_intent', 'reviewer');
    navigate('/become-reviewer');
  };

  const handleSubmitClick = () => {
    setIsVisible(false);
    // GA4: Track modal CTA click + store intent
    trackEvent('modal_cta_click', { intent: 'artist' });
    sessionStorage.setItem('mcm_user_intent', 'artist');
    navigate('/upload');
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay - calm, not aggressive */}
      <div 
        className="fixed inset-0 bg-black/40 z-50 transition-opacity duration-300"
        onClick={handleClose}
        data-testid="intent-modal-overlay"
      />
      
      {/* Modal */}
      <div 
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8 animate-in fade-in zoom-in-95 duration-300"
        data-testid="intent-modal"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
          data-testid="intent-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Headline */}
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-2">
          What are you here to do?
        </h2>
        
        {/* Subheadline */}
        <p className="text-center text-gray-500 mb-8">
          Pick a path. You can switch later.
        </p>

        {/* Option A - Review Music (Primary) */}
        <div className="mb-4">
          <button
            onClick={handleReviewerClick}
            className="w-full bg-black hover:bg-gray-800 text-white font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-lg shadow-lg hover:shadow-xl"
            data-testid="intent-modal-reviewer-btn"
          >
            <Headphones className="h-5 w-5" />
            Review Music & Get Paid
          </button>
          <div className="mt-3 space-y-1 px-2">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
              No audience required
            </p>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
              Earn from tips, skips, and live sessions
            </p>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
              Free to start
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Option B - Submit Music (Secondary) */}
        <div className="mb-6">
          <button
            onClick={handleSubmitClick}
            className="w-full bg-[#F2D857] hover:bg-[#e5cc4d] text-black font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-lg"
            data-testid="intent-modal-submit-btn"
          >
            <Music className="h-5 w-5" />
            Submit My Music
          </button>
          <div className="mt-3 space-y-1 px-2">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
              Live feedback from real reviewers
            </p>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
              Priority options available
            </p>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
              No waiting for emails
            </p>
          </div>
        </div>

        {/* Trust Reinforcement */}
        <div className="border-t border-gray-100 pt-4 flex flex-wrap justify-center gap-x-4 gap-y-2">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Check className="h-3 w-3 text-green-500" />
            Beginner-friendly
          </span>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Check className="h-3 w-3 text-green-500" />
            Positivity-first moderation
          </span>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Check className="h-3 w-3 text-green-500" />
            Real payouts, real sessions
          </span>
        </div>
      </div>
    </>
  );
};

export default IntentModal;
